import { useState, useCallback, useEffect } from 'react';
import { localStorageAvailable, localStorageGetItem } from 'src/utils/storage-available';
import { allLangs, defaultLang } from 'src/locales/config-lang';
import { useSettingsContext } from 'src/components/settings';

// ----------------------------------------------------------------------

const LANGUAGE_KEY = 'foodo-language';

export interface LanguageState {
  currentLang: typeof defaultLang;
  isRTL: boolean;
  allLangs: typeof allLangs;
}

export interface LanguageActions {
  changeLanguage: (langValue: string) => void;
}

export interface LanguageManager extends LanguageState, LanguageActions {}

// Get language from localStorage or default to Arabic
const getStoredLanguage = (): string => {
  if (!localStorageAvailable()) return 'ar';

  const stored = localStorageGetItem(LANGUAGE_KEY, 'ar') || 'ar';
  // Validate that stored language is supported
  const isValidLang = allLangs.some((lang) => lang.value === stored);
  return isValidLang ? stored : 'ar';
};

// Set language in localStorage
const setStoredLanguage = (langValue: string): void => {
  if (!localStorageAvailable()) return;

  // Validate language before storing
  const isValidLang = allLangs.some((lang) => lang.value === langValue);
  if (isValidLang) {
    localStorage.setItem(LANGUAGE_KEY, langValue);
  }
};

// Create language state from language value
const createLanguageState = (langValue: string): LanguageState => {
  const currentLang = allLangs.find((lang) => lang.value === langValue) || defaultLang;
  const isRTL = langValue === 'ar';

  return {
    currentLang,
    isRTL,
    allLangs,
  };
};

// Custom hook for centralized language management
export function useLanguageManager(): LanguageManager {
  const settings = useSettingsContext();

  // Initialize with stored language
  const [languageState, setLanguageState] = useState<LanguageState>(() => {
    const storedLang = getStoredLanguage();
    return createLanguageState(storedLang);
  });

  // Listen for localStorage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LANGUAGE_KEY && e.newValue) {
        const newLang = e.newValue;
        setLanguageState(createLanguageState(newLang));
        // Update theme direction in settings
        settings.onChangeDirectionByLang(newLang);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [settings]);

  // Change language function
  const changeLanguage = useCallback(
    (langValue: string) => {
      // Validate language
      const isValidLang = allLangs.some((lang) => lang.value === langValue);
      if (!isValidLang) {
        console.warn(
          `Invalid language: ${langValue}. Supported languages:`,
          allLangs.map((l) => l.value)
        );
        return;
      }

      // Update localStorage
      setStoredLanguage(langValue);

      // Update local state
      setLanguageState(createLanguageState(langValue));

      // Update theme direction in settings
      settings.onChangeDirectionByLang(langValue);

      // Dispatch custom event for other parts of the app to listen
      window.dispatchEvent(
        new CustomEvent('languageChanged', {
          detail: { language: langValue },
        })
      );

      // Force hard reload to ensure everything updates properly
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    [settings]
  );

  return {
    ...languageState,
    changeLanguage,
  };
}

// Utility functions for external use
export const getLanguage = (): string => getStoredLanguage();
export const setLanguage = (langValue: string): void => setStoredLanguage(langValue);
export const isRTLLanguage = (langValue?: string): boolean => {
  const lang = langValue || getStoredLanguage();
  return lang === 'ar';
};
